// ─────────────────────────────────────────────
//  useImportReviewActions — the three answers a human can give a review item.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Both importers (the per-facility one and the global one) show the same queue
//  and must behave identically, so the answers live here once. A second copy of
//  "what confirming means" is one edit away from two surfaces disagreeing about
//  whether a click created a person.
//
//  ⚠️ CONFIRMING WRITES AN ALIAS, NOT AN ASSIGNMENT. The raw string binds to the
//  record the reviewer chose; the apply re-parses with that alias in hand, and
//  tomorrow's import never asks again. That convergence IS the design — a
//  question answered once stays answered.
//
//  ⚠️ CREATING IS THE ONE DOOR THAT MAKES A PERSON, AND A HUMAN IS STANDING IN
//  IT. No import path reaches `createPerson`: it runs from a two-click
//  confirmation inside the queue, attributed to the administrator who clicked,
//  and it writes an alias alongside so the sheet's spelling resolves next time
//  instead of offering to create the same person again.
// ─────────────────────────────────────────────
import { useCallback, useState } from "react";
import { addStaff, saveStaffAlias } from "../firebase/firestore";
import { useToast } from "../context/ToastContext";

export default function useImportReviewActions() {
  // item id → "confirmed" | "created" | "skipped". A settled question is never
  // re-asked inside one review pass.
  const [answered, setAnswered] = useState({});
  // The gate: an import with open questions cannot be applied until somebody
  // says they read them. The apply behind it FULL-RESETS every board.
  const [acknowledged, setAcknowledged] = useState(false);
  const toast = useToast();

  const reset = useCallback(() => {
    setAnswered({});
    setAcknowledged(false);
  }, []);

  const confirmAlias = useCallback(async (item, candidate) => {
    const res = await saveStaffAlias({
      raw: item.name,
      staffId: candidate.staffId,
      facilityId: item.facilityId,
      source: "import-review",
    });
    if (!res.ok) {
      toast.error(`Could not save that confirmation — ${res.reason}.`);
      return;
    }
    setAnswered((a) => ({ ...a, [item.id]: "confirmed" }));
    toast.success(`"${item.name}" is ${candidate.name}. It resolves silently from now on.`);
  }, [toast]);

  const createPerson = useCallback(async (item) => {
    try {
      const ref = await addStaff({
        name: item.name, role: "crna", primaryFacilityId: item.facilityId,
        source: "import-review",
      });
      await saveStaffAlias({
        raw: item.name, staffId: ref.id, facilityId: item.facilityId, source: "import-review",
      });
      setAnswered((a) => ({ ...a, [item.id]: "created" }));
      toast.success(`Added ${item.name} to the roster.`);
    } catch (e) {
      console.error("[importReview] create person failed:", e);
      toast.error(`Could not add ${item.name} — ${e?.message || "unknown error"}.`);
    }
  }, [toast]);

  const skipItem = useCallback((item) => {
    setAnswered((a) => ({ ...a, [item.id]: "skipped" }));
  }, []);

  return { answered, acknowledged, setAcknowledged, reset, confirmAlias, createPerson, skipItem };
}

/**
 * Is this import safe to apply yet?
 *
 * ⚠️ SEEING THE REPORT IS THE REQUIREMENT, NOT CLEARING IT. The owner's ruling
 * is "I want as much as you can, somebody will review everything before 6" —
 * so an import with open questions still applies. What it may not do is apply
 * while nobody has looked, because the board reset underneath it rebuilds the
 * day without the people nobody was asked about.
 */
export function applyBlockedBy(coverage, acknowledged) {
  const open = Number(coverage?.needsYou || 0) + Number(coverage?.unreadable || 0);
  return open > 0 && !acknowledged ? open : 0;
}
